import { useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Hash, Pencil, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface DocumentSequence {
  id: string;
  documentType: string;
  prefix: string;
  paddingWidth: number;
  yearScoped: boolean;
  formatPattern: string | null;
  currentValue: number;
  isActive: boolean;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  quote: "Sales Quotes",
  order: "Orders",
  purchase_order: "Purchase Orders",
  invoice: "Invoices",
  receipt: "Goods Receipts",
  credit_memo: "Credit Memos",
  blanket_po: "Blanket POs",
  sales_agreement: "Sales Agreements",
  shipment_inbound: "Inbound Shipments",
  shipment_outbound: "Outbound Shipments",
};

function formatPreview(
  prefix: string, padding: number, yearScoped: boolean,
  currentValue: number, formatPattern?: string | null
): string {
  const next = currentValue + 1;
  const numStr = next.toString().padStart(padding, "0");
  const year = new Date().getFullYear().toString();
  if (formatPattern) {
    return formatPattern
      .replace("{PREFIX}", prefix)
      .replace("{YYYY}", year)
      .replace("{NUM}", numStr);
  }
  if (yearScoped) return `${prefix}-${year}-${numStr}`;
  return `${prefix}-${numStr}`;
}

export default function DocumentSequencesSettingsPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [editDialog, setEditDialog] = useState(false);
  const [resetDialog, setResetDialog] = useState(false);
  const [editing, setEditing] = useState<DocumentSequence | null>(null);
  const [form, setForm] = useState({
    prefix: "", paddingWidth: 5, yearScoped: true, formatPattern: "",
  });
  const [resetValue, setResetValue] = useState("0");

  const { data: sequences = [] } = useQuery<DocumentSequence[]>({
    queryKey: ["/api/document-sequences"],
    queryFn: () => api.get<DocumentSequence[]>(`/document-sequences`, sessionId),
  });

  const initializeMutation = useMutation({
    mutationFn: () => api.post(`/document-sequences/initialize`, {}, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/document-sequences"] });
      toast({ title: "Sequences initialized" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!editing) return Promise.resolve(null as any);
      return api.patch(`/document-sequences/${editing.id}`, {
        prefix: form.prefix,
        paddingWidth: form.paddingWidth,
        yearScoped: form.yearScoped,
        formatPattern: form.formatPattern || "",
      }, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/document-sequences"] });
      setEditDialog(false);
      setEditing(null);
      toast({ title: "Sequence updated" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const resetMutation = useMutation({
    mutationFn: () => {
      if (!editing) return Promise.resolve(null as any);
      return api.patch(`/document-sequences/${editing.id}`, {
        currentValue: Number(resetValue) || 0,
      }, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/document-sequences"] });
      setResetDialog(false);
      setEditing(null);
      toast({ title: "Counter reset" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const handleEdit = (s: DocumentSequence) => {
    setEditing(s);
    setForm({
      prefix: s.prefix,
      paddingWidth: s.paddingWidth,
      yearScoped: s.yearScoped,
      formatPattern: s.formatPattern || "",
    });
    setEditDialog(true);
  };

  const handleReset = (s: DocumentSequence) => {
    setEditing(s);
    setResetValue("0");
    setResetDialog(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Document Sequences"
        description="Configure numbering formats for quotes, orders, invoices, and other documents"
        actions={
          <div className="flex items-center gap-2">
            {sequences.length === 0 && (
              <Button
                onClick={() => initializeMutation.mutate()}
                disabled={initializeMutation.isPending}
              >
                {initializeMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Initialize Sequences
              </Button>
            )}
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Hash className="h-4 w-4" /> All Sequences ({sequences.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sequences.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No sequences configured. Click "Initialize Sequences" to create the defaults.
            </p>
          ) : (
            <div className="space-y-2">
              {sequences.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/40"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">
                        {DOC_TYPE_LABELS[s.documentType] || s.documentType}
                      </span>
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{s.prefix}</code>
                      {s.yearScoped && (
                        <Badge variant="outline" className="text-[10px]">Year Scoped</Badge>
                      )}
                      {!s.isActive && (
                        <Badge variant="secondary" className="text-[10px]">Inactive</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs text-muted-foreground">Next:</span>
                      <code className="text-xs text-muted-foreground">
                        {formatPreview(s.prefix, s.paddingWidth, s.yearScoped, s.currentValue, s.formatPattern)}
                      </code>
                      <span className="text-xs text-muted-foreground">· current {s.currentValue}</span>
                      <span className="text-xs text-muted-foreground">· padding {s.paddingWidth}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost" size="icon" className="h-8 w-8"
                      onClick={() => handleEdit(s)}
                      title="Edit format"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="icon" className="h-8 w-8"
                      onClick={() => handleReset(s)}
                      title="Reset counter"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Edit {editing ? (DOC_TYPE_LABELS[editing.documentType] || editing.documentType) : ""} Sequence
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Editing the prefix or format will affect new documents only. Existing numbers are unchanged.
          </p>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Prefix</label>
              <Input
                value={form.prefix}
                onChange={(e) => setForm({ ...form, prefix: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Padding Width</label>
              <Input
                type="number" min={1} max={10}
                value={form.paddingWidth}
                onChange={(e) => setForm({ ...form, paddingWidth: Number(e.target.value) || 1 })}
              />
            </div>
            <label className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <span className="text-sm font-medium">Year Scoped</span>
                <p className="text-xs text-muted-foreground">
                  Reset numbering each year and include the year in the format.
                </p>
              </div>
              <input
                type="checkbox"
                checked={form.yearScoped}
                onChange={(e) => setForm({ ...form, yearScoped: e.target.checked })}
              />
            </label>
            <div>
              <label className="text-sm font-medium mb-1 block">Format Pattern (optional)</label>
              <Input
                className="font-mono"
                value={form.formatPattern}
                onChange={(e) => setForm({ ...form, formatPattern: e.target.value })}
                placeholder="{PREFIX}-{YYYY}-{NUM}"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Tokens: <code>{"{PREFIX}"}</code>, <code>{"{YYYY}"}</code>, <code>{"{NUM}"}</code>. Leave blank for default.
              </p>
            </div>
            <div className="rounded-lg bg-muted/50 p-3">
              <p className="text-xs text-muted-foreground mb-1">Preview of next number</p>
              <code className="text-sm font-mono">
                {formatPreview(form.prefix, form.paddingWidth, form.yearScoped, editing?.currentValue || 0, form.formatPattern || null)}
              </code>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
              <Button
                onClick={() => updateMutation.mutate()}
                disabled={!form.prefix || updateMutation.isPending}
              >
                {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset counter dialog */}
      <Dialog open={resetDialog} onOpenChange={setResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Counter</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Reset the counter for {editing ? (DOC_TYPE_LABELS[editing.documentType] || editing.documentType) : ""}.
            This will change the next document number. Existing documents are not affected.
          </p>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium mb-1 block">New counter value</label>
              <Input
                type="number" min={0}
                value={resetValue}
                onChange={(e) => setResetValue(e.target.value)}
              />
            </div>
            {editing && (
              <p className="text-xs text-muted-foreground">
                Next number will be:{" "}
                <code className="font-mono">
                  {formatPreview(editing.prefix, editing.paddingWidth, editing.yearScoped, Number(resetValue) || 0, editing.formatPattern)}
                </code>
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setResetDialog(false)}>Cancel</Button>
              <Button
                onClick={() => resetMutation.mutate()}
                disabled={resetMutation.isPending}
              >
                {resetMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Reset Counter
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
